/**
 * Which paths render WITHOUT a session.
 *
 * Split out of App.tsx because that file reached the 500-line ceiling the
 * `fileSize` ratchet enforces once this branch and main both grew it. A good
 * seam rather than an arbitrary one: this is a pure predicate over a pathname,
 * it is the single fence that decides whether a visitor is bounced to /login,
 * and its reasoning is long because every entry here was earned.
 *
 * READ THIS BEFORE ADDING A PUBLIC PAGE. Declaring a route in the public tree
 * is NOT enough — this list is what actually keeps a logged-out visitor out of
 * /login, and a page that forgets it bounces the very people it was built for.
 * Three shipped that way (verify-email, oauth/consent, the board form) and each
 * looked completely fine in review.
 *
 * UPDATED 2026-09: Unknown paths for LOGGED-OUT visitors now render the public
 * tree (landing page catch-all) instead of redirecting to /login. This matches
 * user expectations: /pricing or /nonexistent-xyz → landing page, not a login
 * prompt. Known app routes (e.g. /business, /settings/billing) still redirect
 * to /login to preserve the ?redirect= flow.
 *
 * Rule summary:
 *   - unknown path + no session → public → landing page
 *   - unknown path + session → protected tree → /business (unchanged)
 *   - known app route + no session → /login?redirect= (unchanged)
 */
import { isKnownAppPath } from './appRoutePatterns';
import { getAuthToken, hasRefreshToken } from '@/services/api';

/**
 * Returns true if the path is in the static allowlist of public paths.
 * This is the original isPublicPath logic before the unknown-path rule.
 */
export function isStaticPublicPath(pathname: string): boolean {
  return (
    pathname === '/' ||
      pathname === '/landing' ||
      pathname === '/auth' ||
      pathname.startsWith('/auth/') ||
      pathname === '/login' ||
      pathname === '/signup' ||
      pathname === '/onboarding' ||
      pathname === '/forgot-password' ||
      pathname === '/reset-password' ||
      // Email verification: the destination of the verify link we mail out. It
      // was declared in the public tree but missing from this allowlist, so it
      // never reached it — logged-out visitors were sent to /login and logged-in
      // ones fell through to the landing page. Neither ever verified the address.
      pathname === '/verify-email' ||
      // OAuth consent: a third-party app sends the user here, necessarily while
      // signed in. Same omission, same bounce to the landing page.
      pathname === '/oauth/consent' ||
      // OAuth authorize: browser hosts (the Chrome extension, desktop apps)
      // cannot send the session header, so the API hands them here; this page
      // holds the session and forwards them to consent.
      pathname === '/oauth/authorize' ||
      // Device connect (RFC 8628): a terminal's login link lands here, signed
      // in or not — the page routes through /login and back by itself.
      pathname === '/connect' ||
      // Developer docs (/docs/claude, /docs/mcp) — public reference pages.
      pathname.startsWith('/docs/') ||
      pathname === '/privacy' ||
      pathname === '/terms' ||
      pathname === '/cookies' ||
      pathname === '/acceptable-use' ||
      pathname === '/accessibility-statement' ||
      pathname === '/getting-started' ||
      // Landing section deep links (app/marketingRoutes.tsx) — same page as
      // "/", must stay public so refresh and logged-in visitors keep the URL.
      pathname === '/faq' ||
      pathname === '/channels' ||
      pathname === '/flows' ||
      // The free Automation Playbook (app/marketingRoutes.tsx) — the gift the
      // Instagram/WhatsApp flows send to people who have no account yet.
      pathname === '/playbook' ||
      pathname.startsWith('/playbook/') ||
      pathname === '/data-deletion-status' ||
      pathname === '/data-status' ||
      pathname.startsWith('/vendor/') ||
      pathname.startsWith('/admin') ||
      pathname.startsWith('/shopify') ||
      // BuzzCall (W2): guests join by magic link — no login, no AppLayout
      pathname.startsWith('/call/') ||
      // Board Forms: public capture page (was missing here — authed visitors
      // were bounced to the landing page instead of the form)
      pathname.startsWith('/f/') ||
      // Events (V1a): public registration page
      pathname.startsWith('/e/') ||
      // Native booking page (V1b): public slot booking
      // The routing form that FEEDS /book/. Declaring the route in the public
      // tree is not enough — this allowlist is what actually keeps a logged-out
      // visitor out of /login, and every public page that forgot it (verify-email,
      // oauth/consent) bounced its visitors instead of serving them.
      pathname.startsWith('/r/') ||
      pathname.startsWith('/book/') ||
      // E20 manage link. NOT covered by '/book/' above — '/booking/x' does not
      // start with it — and this page's premise is that there is no account.
      pathname.startsWith('/booking/') ||
      // BuzzPage (V3a): public link-in-bio front door
      pathname.startsWith('/p/') ||
      // Members area (V4): magic-link lesson pages
      pathname.startsWith('/m/') ||
      // Branded checkout (PAY-2): merchant-branded payment page
      pathname.startsWith('/pay/') ||
      // Team invites: the emailed accept page — must render logged-out
      pathname.startsWith('/invite/') ||
      // BuzzTalk guest invites: the emailed magic link. A guest is external by
      // definition, so this one has to render with no session at all.
      pathname.startsWith('/chat/accept/')
  );
}

/**
 * Returns true if there's an active or renewable session.
 */
function hasSession(): boolean {
  return !!getAuthToken() || hasRefreshToken();
}

/**
 * Returns true if the pathname should render the public route tree.
 *
 * The logic:
 * 1. Static public paths (login, privacy, booking pages, etc.) → always public
 * 2. Unknown paths (not in app patterns) + no session → public (landing page)
 * 3. Known app routes + no session → NOT public (→ /login?redirect=)
 * 4. Any path + session → NOT public (→ protected tree)
 */
export function isPublicPath(pathname: string): boolean {
  // Static allowlist always wins — these paths are public regardless of session
  if (isStaticPublicPath(pathname)) {
    return true;
  }

  // With a session, unknown paths go to the protected tree (/business fallback)
  if (hasSession()) {
    return false;
  }

  // No session + unknown path → render public tree (landing page catch-all)
  // No session + known app path → redirect to /login?redirect=
  return !isKnownAppPath(pathname);
}
